/**
 * Credit Warning Banner Component
 *
 * Displays a dismissible banner at the top of the page when credits are low or critical.
 * The banner reappears on page reload to ensure visibility.
 */

import React, { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AlertTriangle, TrendingDown, X } from 'lucide-react';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';
import { getCreditWarningMessage } from '@/utils/creditWarnings';

export const CreditWarningBanner: React.FC = () => {
  const { totalAvailableCredits, isLoading, isEnabled } = usePractitionerCredits();
  const { hasPractitionerAccess } = useUserRole();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const userType = hasPractitionerAccess ? 'practitioner' : 'individual';
  const warningInfo = getCreditWarningMessage(totalAvailableCredits, userType);

  // Don't show if loading (credits default to 0), user not authenticated, no warning, or if dismissed
  if (!user || !isEnabled || isLoading || !warningInfo || dismissed) return null;

  const Icon = warningInfo.variant === 'destructive' ? AlertTriangle : TrendingDown;

  return (
    <Alert
      variant={warningInfo.variant}
      className="mb-4 border-l-4 shadow-md relative"
    >
      <Icon className="h-5 w-5" />
      <div className="flex items-start justify-between flex-1 pr-8">
        <div className="flex-1">
          <AlertTitle className="font-bold text-base mb-1">
            {warningInfo.title}
          </AlertTitle>
          <AlertDescription className="text-sm">
            {warningInfo.message}
          </AlertDescription>
        </div>
        <div className="ml-4 flex-shrink-0">
          <Button asChild size="sm" variant="default">
            <Link to="/preferences?tab=credits">
              Purchase Credits
            </Link>
          </Button>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss warning"
      >
        <X className="h-4 w-4" />
      </Button>
    </Alert>
  );
};
